import { C } from '../theme'
import { usd } from '../lib/format'
import type { AcceptanceWindow, Snapshot } from '../types'

/** Приёмка считается по солверам: венью грейдит каждого отдельно. */
export function acceptanceOf(w: AcceptanceWindow) {
  const answered = w.solvers.reduce((n, s) => n + (s.answered ?? 0), 0)
  const graded = w.solvers.reduce((n, s) => n + s.graded, 0)
  const measured = w.solvers.filter((s) => s.answered != null && s.graded > 0)
  const worst = measured.reduce<(typeof measured)[number] | null>(
    (acc, s) =>
      acc == null || (s.answered ?? 0) / s.graded < (acc.answered ?? 0) / acc.graded ? s : acc,
    null,
  )
  return {
    answered,
    graded,
    pct: graded ? (answered / graded) * 100 : 0,
    worst,
    worstPct: worst ? ((worst.answered ?? 0) / worst.graded) * 100 : null,
  }
}

function Cell({
  label,
  children,
  sub,
}: {
  label: string
  children: React.ReactNode
  sub?: React.ReactNode
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 12,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', marginTop: 4, whiteSpace: 'nowrap' }}>
        {children}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.muted2, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

export function Position({ snap }: { snap: Snapshot }) {
  const d = snap.derived
  const acc = acceptanceOf(snap.acceptance['90s'])

  // Дорожка размечена до порога ликвидации: без него ни наш колпак, ни стена
  // контракта не имеют масштаба.
  const fill = Math.min(100, (d.ltvPct / d.liquidationPct) * 100)
  const capMark = (d.ownCapPct / d.liquidationPct) * 100
  const wallMark = (d.contractWallPct / d.liquidationPct) * 100

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          rowGap: 6,
          flexWrap: 'wrap',
          borderBottom: `4px solid ${C.accent}`,
          padding: '22px 0 12px',
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '.22em',
            textTransform: 'uppercase',
            color: C.accent,
          }}
        >
          Position
        </span>
        <span
          style={{
            fontSize: 13,
            color: C.muted,
            whiteSpace: 'nowrap',
          }}
        >
          {d.usdRefAsset} taken as $1 · pinned to one block
        </span>
      </div>

      <div
        data-hero
        style={{
          display: 'grid',
          gridTemplateColumns: '1.25fr 1fr 1fr 1fr',
          gap: 28,
          padding: '18px 0 4px',
          alignItems: 'start',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: C.muted,
            }}
          >
            LTV
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap' }}>
            {d.ltvPct.toFixed(2)}
            <span style={{ fontSize: 15, color: C.muted }}>%</span>
            <span style={{ fontSize: 13, fontWeight: 400, color: C.muted2, paddingLeft: 10 }}>
              cap {d.ownCapPct}% · wall {d.contractWallPct.toFixed(0)}% · liq{' '}
              {d.liquidationPct.toFixed(0)}%
            </span>
          </div>
          <div style={{ position: 'relative', height: 8, background: C.track, marginTop: 10 }}>
            <div
              style={{ position: 'absolute', inset: 0, width: `${fill}%`, background: C.accent }}
            />
            <div
              title={`our cap ${d.ownCapPct}%`}
              style={{ position: 'absolute', left: `${capMark}%`, top: -3, bottom: -3, width: 2, background: C.text }}
            />
            <div
              title={`contract wall ${d.contractWallPct}%`}
              style={{ position: 'absolute', left: `${wallMark}%`, top: -3, bottom: -3, width: 2, background: C.warn }}
            />
          </div>
        </div>

        <Cell
          label="Room to quote"
          sub={`${d.headroomPp.toFixed(2)} pp before our own cap`}
        >
          <span style={{ color: C.accent }}>{usd(d.headroomUsd)}</span>
        </Cell>

        {/* Их «health factor» рядом с настоящим запасом — иначе первое читается
            как расстояние до ликвидации, которым оно не является. */}
        <Cell
          label="Health factor"
          sub={
            <>
              their formula: collateral ÷ debt ·{' '}
              <span style={{ color: C.text }}>
                {(d.liquidationPct - d.ltvPct).toFixed(1)} pp to liquidation
              </span>
            </>
          }
        >
          <span style={{ color: d.venueHealthFactor < 1.2 ? C.neg : C.pos }}>
            {d.venueHealthFactor.toFixed(2)}
          </span>
        </Cell>

        <Cell
          label="Accepted, 90 s"
          sub={
            acc.worst && acc.worstPct != null ? (
              <>
                {acc.answered} of {acc.graded} · worst{' '}
                <span style={{ color: acc.worstPct < 90 ? C.neg : C.text }}>
                  {acc.worst.solver.replace('_integration', '')} {acc.worstPct.toFixed(0)}%
                </span>
              </>
            ) : null
          }
        >
          {acc.pct.toFixed(1)}
          <span style={{ fontSize: 15, color: C.muted }}>%</span>
        </Cell>
      </div>
    </>
  )
}
